import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = () => ({ name: "", target_amount: "", saved_amount: "0", deadline: "" });

export default function Goals() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/goals");
      setItems(r.data);
    } catch (e) {
      console.warn("Goals load failed", e?.response?.status);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(empty());
    setOpen(true);
  };

  const openEdit = (g) => {
    setEditingId(g.id);
    setForm({
      name: g.name,
      target_amount: String(g.target_amount),
      saved_amount: String(g.saved_amount),
      deadline: g.deadline || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.target_amount || Number(form.target_amount) <= 0) {
      toast.error("Name and target are required");
      return;
    }
    const payload = {
      name: form.name.trim(),
      target_amount: Number(form.target_amount),
      saved_amount: Number(form.saved_amount || 0),
      deadline: form.deadline || null,
    };
    try {
      if (editingId) {
        await api.put(`/goals/${editingId}`, payload);
        toast.success("Goal updated");
      } else {
        await api.post("/goals", payload);
        toast.success("Goal created");
      }
      setOpen(false);
      load();
    } catch {
      toast.error("Could not save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this goal?")) return;
    await api.delete(`/goals/${id}`);
    toast.success("Goal removed");
    load();
  };

  return (
    <div data-testid="goals-page">
      <SectionHeader
        overline="Aspirations"
        title="Savings Goals"
        description="Name what you're saving toward. Watch the bar grow, slowly."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={openCreate}
                data-testid="open-add-goal"
                className="bg-[#1F4E3D] hover:bg-[#163B2E] text-white"
              >
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> New goal
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-tight font-medium">
                  {editingId ? "Edit goal" : "New goal"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Name</Label>
                  <Input
                    data-testid="goal-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Trip to Ladakh"
                    className="mt-1.5"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Target (₹)</Label>
                    <Input
                      data-testid="goal-target"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.target_amount}
                      onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
                      className="mt-1.5 font-mono-num"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Saved (₹)</Label>
                    <Input
                      data-testid="goal-saved"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.saved_amount}
                      onChange={(e) => setForm({ ...form, saved_amount: e.target.value })}
                      className="mt-1.5 font-mono-num"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Deadline</Label>
                  <Input
                    data-testid="goal-deadline"
                    type="date"
                    value={form.deadline}
                    onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button
                    type="submit"
                    data-testid="save-goal"
                    className="bg-[#1F4E3D] hover:bg-[#163B2E] text-white"
                  >
                    Save
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {items.length === 0 ? (
        <div className="bg-white border border-[#EAE8E3] rounded-lg p-12 text-center text-sm text-[#696E69]">
          No goals yet. Name something you're saving for.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((g) => {
            const pct = g.target_amount > 0 ? (g.saved_amount / g.target_amount) * 100 : 0;
            return (
              <div
                key={g.id}
                className="bg-white border border-[#EAE8E3] rounded-lg p-6"
                data-testid={`goal-card-${g.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="label-overline mb-1">Goal</div>
                    <h3 className="font-display text-xl tracking-tight">{g.name}</h3>
                    {g.deadline && (
                      <div className="text-xs text-[#696E69] mt-1 font-mono-num">
                        by {g.deadline}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEdit(g)}
                      data-testid={`edit-goal-${g.id}`}
                      className="p-2 rounded-md hover:bg-[#F3F1ED] text-[#696E69]"
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => remove(g.id)}
                      data-testid={`delete-goal-${g.id}`}
                      className="p-2 rounded-md hover:bg-[#D26A59]/10 text-[#D26A59]"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
                <div className="font-mono-num text-2xl tracking-tight mb-3">
                  ₹{formatINR(g.saved_amount)}
                  <span className="text-[#696E69] text-sm ml-1">/ ₹{formatINR(g.target_amount)}</span>
                </div>
                <div className="h-2 w-full bg-[#F3F1ED] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#5C8065] rounded-full transition-all"
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-[#696E69] font-mono-num">
                  <span>{pct.toFixed(0)}% saved</span>
                  <span>₹{formatINR(Math.max(g.target_amount - g.saved_amount, 0))} to go</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
